import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { toastManager } from '@crm/entities/toast';
import { getApiErrorMessage } from '@crm/shared/api';

import { dealApi } from './api-handlers';
import { dealQueryKeys } from './query-keys';

import type {
    TInWorkOrFinishedDealsData,
    TUpdateInWorkOrFinishedDealParams,
} from '../model/in-work-or-finished-deals';

export const useUpdateInWorkOrFinishedDealMutation = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: dealQueryKeys.inWorkOrFinishedDeals.update(),
        mutationFn: (params: TUpdateInWorkOrFinishedDealParams) =>
            dealApi.updateInWorkOrFinishedDeal(params),
        onSuccess: (updatedDeal) => {
            queryClient.setQueriesData<TInWorkOrFinishedDealsData>(
                { queryKey: dealQueryKeys.inWorkOrFinishedDeals.all() },
                (currentData) => {
                    if (!currentData) {
                        return currentData;
                    }

                    const updatedDealIndex = currentData.items.findIndex(
                        (deal) => deal.id === updatedDeal.id
                    );

                    if (updatedDealIndex === -1) {
                        return currentData;
                    }

                    const items = [...currentData.items];
                    items[updatedDealIndex] = updatedDeal;

                    return { ...currentData, items };
                }
            );
        },
        onError: (error) => {
            toastManager.onError({
                title: getApiErrorMessage(error, t('crm.common.deal.errors.update')),
            });
        },
    });
};
